import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container, Flex, Stack, Text, Title } from '@mantine/core';
import {
    IconBodyScan,
    IconChartArcs,
    IconCheckupList,
    IconGraph,
    IconHeartbeat,
    IconReportMedical,
    IconBook,
    IconUsers,
} from '@tabler/icons-react';

import Shell from '../../global/component/organism/Shell/Shell';
import { ITabsProps } from '../../global/component/organism/SideBar/SideBar';
import { PatientStore } from '../../global/Store/PatientStore.ts';
import { inject, observer } from 'mobx-react';
import Utils from '../../global/helpers/utils.ts';
import classes from '../../global/style/Classes.module.less';
import { useTranslation } from 'react-i18next';
import { useFeatureVisibility } from '../../global/provider/featureVisibilityContext';

export type ILHcpPatientDashboardShellProps = {
    patientStore?: PatientStore;
};

const HcpPatientDashboardShell: React.FC<ILHcpPatientDashboardShellProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const location = useLocation();
        const { t, i18n } = useTranslation();
        const { isFeatureVisible, isLoading: isLoadingFeatures } = useFeatureVisibility();
        const navigate = useNavigate();

        const segments = location.pathname.split('/').filter(Boolean);

        const hcpPatientDashboardTabs: ITabsProps[] = useMemo(
            () => [
                {
                    ids: ['medicalInformation', 'editPrescription', 'lifeEvents', 'mmeCalculator'],
                    icon: IconReportMedical,
                    title: t('navbar.hcpMedicalInformation'),
                    featureCode: 'medicalInformation',
                },
                { ids: ['main'], icon: IconChartArcs, title: t('navbar.hcpMedicalOverview'), featureCode: 'medicalOverview' },
                { ids: ['dosageModeling'], icon: IconGraph, title: t('navbar.hcpDosageModeling'), featureCode: 'simulations' },
                { ids: ['task', 'surveyResult'], icon: IconCheckupList, title: t('page.task.task'), featureCode: 'tasks' },
                { ids: ['physiologicalData'], icon: IconHeartbeat, title: t('navbar.physiologicalData'), featureCode: 'physiologicals' },
                { ids: ['biomarkers'], icon: IconBodyScan, title: t('navbar.biomarkers'), featureCode: 'labResults' },
                { ids: ['journals'], icon: IconBook, title: t('navbar.journals'), featureCode: 'journal' },
                { ids: ['delegations'], icon: IconUsers, title: t('navbar.delegations') },
            ],
            [t, i18n.language],
        );

        const filteredTabs = useMemo(
            () => hcpPatientDashboardTabs.filter(tab => !tab.featureCode || isFeatureVisible(tab.featureCode)),
            [hcpPatientDashboardTabs, isFeatureVisible],
        );

        const firstAvailableRoute = filteredTabs[0]?.ids[0];

        // Check if the current route belongs to a disabled feature
        const isCurrentRouteAccessible = useMemo(() => {
            const currentSegment = segments[1];
            if (!currentSegment) return true;

            const owningTab = hcpPatientDashboardTabs.find(tab => tab.ids.includes(currentSegment));
            if (!owningTab?.featureCode) return true;
            return isFeatureVisible(owningTab.featureCode);
        }, [segments, hcpPatientDashboardTabs, isFeatureVisible]);

        // Redirect to the first available tab when on a disabled feature route
        useEffect(() => {
            if (!isLoadingFeatures && !isCurrentRouteAccessible && firstAvailableRoute) 
                navigate(firstAvailableRoute, { replace: true });
            
        }, [isCurrentRouteAccessible, isLoadingFeatures, firstAvailableRoute, navigate]);

        const title = useMemo(() => {
            return filteredTabs.find((tab) => tab.ids.includes(segments[1]))?.title;
        }, [t, i18n.language, location, filteredTabs]);

        return (
            <Shell tabs={filteredTabs} route="hcpPatientDashboard">
                <Container size="xl" className={classes.shellContainer} style={{ overflowY: 'auto' }}>
                    <Stack pt={'xl'} gap={'lg'}>
                        <Stack
                            hidden={segments.includes('changePassword')}
                            gap={0}
                            align={'flex-start'}
                            justify={'flex-start'}
                        >
                            <Title>{title}</Title>
                            <Flex align={'center'}>
                                <Text size={'sm'}>{patientStore?.name}</Text>
                                {patientStore?.birthDate && patientStore.birthDate !== '-' && (
                                    <>
                                        <Text mx={'xs'} c={'dimmed'}>
                                            {'|'}
                                        </Text>
                                        <Text size={'sm'}>
                                            {`${t('formProfile.birthDate')} ${Utils.formatDateToYYYYMMDD(new Date(patientStore.birthDate))}`}
                                        </Text>
                                    </>
                                )}
                            </Flex>
                        </Stack>
                        {isCurrentRouteAccessible ? <Outlet /> : null}
                    </Stack>
                </Container>
            </Shell>
        );
    })
);

export default HcpPatientDashboardShell;
